'use client';

import type { Action, State, Toast, ToasterToast } from './types';

import { useEffect, useState } from 'react';

import { TOAST_LIMIT } from './constants';

let count = 0;
let memoryState: State = { toasts: [] };

const listeners: Array<(state: State) => void> = [];
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      toastId,
      type: 'REMOVE_TOAST',
    });
  }, 100);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST': {
      const toastLimit = action.toast.toastLimit ?? TOAST_LIMIT;
      const existingIndex = state.toasts.findIndex(
        t => t.id === action.toast.id,
      );
      let newToasts;
      if (existingIndex !== -1) {
        newToasts = [
          // Creates a new toast object by merging the existing toast's properties with the new properties from action.toast
          { ...state.toasts[existingIndex], ...action.toast },
          ...state.toasts.filter((_, i) => i !== existingIndex),
        ];
      } else {
        newToasts = [{ ...action.toast }, ...state.toasts];
      }
      return {
        ...state,
        toasts: newToasts.slice(0, toastLimit),
      };
    }

    case 'DISMISS_TOAST': {
      const { toastId } = action;
      if (toastId == null) {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      } else {
        addToRemoveQueue(toastId);
      }
      return {
        ...state,
        toasts: state.toasts.map(t =>
          t.id === toastId ? { ...t, open: false } : t,
        ),
      };
    }

    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter(t => t.id !== action.toastId),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map(t =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    default:
      return state;
  }
};

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

function toast({ ...props }: Toast) {
  const id = props.id ?? genId();

  const formattedProps = {
    ...props,
    dismissOnRouteChange: props.dismissOnRouteChange ?? true,
    duration: props.duration ?? 6000,
    ...(Boolean(props.action) && {
      darkOverlay: props?.darkOverlay ?? true,
      duration: props.duration ?? Infinity,
      hideCloseButton: props.hideCloseButton ?? true,
    }),
  };

  const update = (props: ToasterToast) =>
    dispatch({
      toast: { ...props, id },
      type: 'UPDATE_TOAST',
    });
  const dismiss = () => dispatch({ toastId: id, type: 'DISMISS_TOAST' });

  dispatch({
    toast: {
      ...formattedProps,
      id,
      onOpenChange: (open) => {
        if (!open) {
          dismiss();
        }
      },
      open: true,
    },
    type: 'ADD_TOAST',
  });

  if (formattedProps.duration !== Infinity) {
    setTimeout(() => {
      dismiss();
    }, formattedProps.duration);
  }

  return {
    dismiss,
    id,
    update,
  };
}

function useToast() {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    dismiss: (toastId?: string) => {
      dispatch({
        toastId: toastId === undefined ? undefined : toastId,
        type: 'DISMISS_TOAST',
      });
    },
    remove: (toastId: string) => {
      dispatch({ toastId, type: 'DISMISS_TOAST' });
      setTimeout(() => {
        dispatch({ toastId, type: 'REMOVE_TOAST' });
      }, 100);
    },
    removeAllToasts: () => {
      dispatch({
        type: 'REMOVE_TOAST',
      });
    },
    toast,
    update: (toastId?: string, toast?: Omit<ToasterToast, 'id'>) => {
      dispatch({
        toast: { ...toast, id: toastId ?? '' },
        type: 'UPDATE_TOAST',
      });
    },
  };
}

export { toast, useToast };
